'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { reportError } from '@/lib/errorReportingService';

// ============================================================================
// useErrorReport — shared hook for auto-reporting errors.
//
// Used by Next.js route-level error.tsx pages and any function component that
// needs to report an error and display the reference number. The class-based
// ErrorBoundary handles its own reporting (it can't use hooks) but shares the
// same reportError service.
// ============================================================================

export interface UseErrorReportOptions {
  /** The error to report. When this changes, a new report is submitted. */
  error: Error & { digest?: string };
  /** Component or module name for the report (e.g. "TotalFinanceModule"). */
  componentName: string;
  /** Human-readable description of what was happening. */
  userAction?: string;
  /** Additional context merged into the report payload. */
  additionalContext?: Record<string, unknown>;
  /** Disable auto-reporting (e.g. for testing). */
  skipReporting?: boolean;
}

export interface UseErrorReportResult {
  /** Reference number returned by the reporting service, or null. */
  reportReference: string | null;
  /** True while the report is being submitted. */
  isReporting: boolean;
}

export function useErrorReport({
  error,
  componentName,
  userAction,
  additionalContext,
  skipReporting = false,
}: UseErrorReportOptions): UseErrorReportResult {
  const [reportReference, setReportReference] = useState<string | null>(null);
  const [isReporting, setIsReporting] = useState(false);
  // Track which error we've already reported so React Strict Mode double-mount
  // doesn't submit the same report twice.
  const reportedErrorRef = useRef<Error | null>(null);

  const handleReport = useCallback(async () => {
    if (skipReporting) return;
    setIsReporting(true);
    try {
      const result = await reportError(
        error,
        componentName,
        userAction ?? `Error caught by ${componentName}`,
        {
          digest: error.digest,
          url: typeof window !== 'undefined' ? window.location.href : 'unknown',
          ...additionalContext,
        },
      );

      if (result.success && result.referenceNumber) {
        setReportReference(result.referenceNumber);
      }
    } catch (err) {
      console.error(`[${componentName}] Failed to report error:`, err);
    } finally {
      setIsReporting(false);
    }
  }, [error, componentName, userAction, additionalContext, skipReporting]);

  useEffect(() => {
    // Log the error with full context
    console.error(`[${componentName}] Error:`, {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      name: error.name,
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      timestamp: new Date().toISOString(),
    });

    // Only auto-report if we haven't already reported this exact error instance
    if (reportedErrorRef.current !== error) {
      reportedErrorRef.current = error;
      void handleReport();
    }
  }, [error, componentName, handleReport]);

  return { reportReference, isReporting };
}
